import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import api, { formatApiError } from "@/utils/api";
import { Section } from "@/components/dashboard/DashboardPrimitives";
import { downloadBlob, downloadCertificate, RESULT_COPY } from "@/components/quiz/quizUtils";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-xl bg-[#0E1A3A] border border-slate-800 p-4">
    <p className="text-xs text-slate-400">{label}</p>
    <p className="text-2xl font-semibold text-white" data-testid={testId}>{value}</p>
  </div>
);

export default function QuizResultsPanel() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/api/quiz/admin/attempts").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e))); }, []);

  const rows = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (data?.items || []).filter((r) => !t || [r.full_name, r.email, r.phone, r.login_email, r.login_name].some((v) => (v || "").toLowerCase().includes(t)));
  }, [data, q]);

  const exportXlsx = () => downloadBlob("/api/quiz/admin/export", "tots360_parent_quiz_results.xlsx").catch((e) => toast.error(formatApiError(e)));

  return (
    <Section title="Parent Quiz results" description="Everyone who has completed the free parent quiz." testid="admin-quiz-section"
      right={<button onClick={exportXlsx} className="text-sm px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white inline-flex items-center gap-1.5" data-testid="admin-quiz-export-btn"><Download className="w-4 h-4" /> Excel</button>}>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Stat label="Tests completed" value={data?.total ?? "–"} testId="admin-quiz-total" />
        <Stat label="High performers" value={data?.high_performers ?? "–"} testId="admin-quiz-high" />
        <Stat label="Average score" value={data ? `${data.average_score}/10` : "–"} testId="admin-quiz-avg" />
      </div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or phone"
        className="w-full mb-3 px-3 py-2 text-sm rounded bg-[#0E1A3A] border border-slate-700 text-slate-100 focus:border-sky-400 outline-none" data-testid="admin-quiz-search-input" />
      <div className="rounded-xl bg-[#0E1A3A] border border-slate-800 overflow-x-auto">
        <table className="w-full text-sm min-w-[960px]" data-testid="admin-quiz-table">
          <thead className="text-xs text-slate-400 text-left">
            <tr className="border-b border-slate-800">{["Name", "Email", "Phone", "Login account", "Score", "Result", "Date", "Code", ""].map((h) => <th key={h} className="px-4 py-2 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-6 text-slate-400">No quiz results yet.</td></tr>}
            {rows.map((r) => {
              const res = RESULT_COPY[r.result] || RESULT_COPY.good;
              return (
                <tr key={r.attempt_id} className="border-b border-slate-800/70 last:border-0" data-testid={`admin-quiz-row-${r.attempt_id}`}>
                  <td className="px-4 py-2 text-white">{r.full_name}{r.attempt_no > 1 && <span className="text-xs text-slate-500"> · #{r.attempt_no}</span>}</td>
                  <td className="px-4 py-2 text-slate-300">{r.email}</td>
                  <td className="px-4 py-2 text-slate-300">{r.phone}</td>
                  <td className="px-4 py-2 text-xs text-slate-400" data-testid={`admin-quiz-login-${r.attempt_id}`}>
                    <div className="text-slate-200">{r.login_name}</div>
                    <div>{r.login_email}{r.login_phone ? ` · ${r.login_phone}` : ""}</div>
                  </td>
                  <td className="px-4 py-2 text-white font-semibold">{r.score}/{r.total}</td>
                  <td className="px-4 py-2"><span className="text-[11px] uppercase px-2 py-0.5 rounded" style={{ color: res.color, background: `${res.color}22` }}>{res.label}</span></td>
                  <td className="px-4 py-2 text-slate-400 whitespace-nowrap">{new Date(r.completed_at).toLocaleDateString()}</td>
                  <td className="px-4 py-2 text-slate-300 font-mono text-xs">{r.personal_code}</td>
                  <td className="px-4 py-2"><button onClick={() => downloadCertificate(r).catch((e) => toast.error(formatApiError(e)))} className="text-xs text-sky-300 hover:text-sky-200" data-testid={`admin-quiz-cert-${r.attempt_id}`}>Certificate</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
